import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import "moment/locale/th";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";

// @material-ui/icons
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import CommuteIcon from "@material-ui/icons/Commute";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import PersonIcon from "@material-ui/icons/Person";
import EventNoteIcon from "@material-ui/icons/EventNote";
import LockIcon from "@material-ui/icons/Lock";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ViewListIcon from "@material-ui/icons/ViewList";
import CloseIcon from "@material-ui/icons/Close";

// FullCalendar components & plugins (aligned with Home.js)
import Fullcalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

// Core components
import Grid from "components/Grid/GridContainer";
import Col from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";

// Utilities & Services
import axios from "utils/httpClient";
import getCurrentUser from "utils/getCurrentUser";
import { isOneWayBooking, formatVehicleEventTime } from "utils/vehicleBooking";

// Support both ESM and CJS environments
const CalendarComponent = typeof Fullcalendar === "function" ? Fullcalendar : (Fullcalendar?.default || Fullcalendar);
const FullCalendar = CalendarComponent;

const useStyles = makeStyles(theme => ({
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "400",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
    textDecoration: "none"
  },
  cardCategoryWhite: {
    color: "rgba(255,255,255,.75)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0"
  },
  tabsHeader: {
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6
  },
  tabLabel: {
    fontFamily: '"Sarabun", sans-serif',
    fontSize: 15,
    fontWeight: 600,
    textTransform: "none"
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 8px 8px 8px"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12
  },
  table: {
    minWidth: 1100
  },
  tableHeadCell: {
    fontWeight: 700,
    backgroundColor: "#f1f5f9",
    color: "#334155",
    whiteSpace: "nowrap",
    fontSize: 14
  },
  tableCell: {
    fontSize: 14,
    color: "#1e293b",
    padding: "12px 14px"
  },
  legBox: {
    marginBottom: 6,
    padding: "6px 10px",
    backgroundColor: "#f8fafc",
    borderRadius: 6,
    border: "1px solid #e2e8f0"
  },
  assignedCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 10px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    flex: "1 1 0px",
    minWidth: 145,
    margin: 0
  },
  assignedCardPending: {
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    flex: "1 1 0px",
    minWidth: 145,
    margin: 0
  },
  assignedCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4
  },
  assignedBadgeRoundTrip: {
    backgroundColor: "#ede9fe",
    color: "#6d28d9",
    fontWeight: 700,
    fontSize: 11,
    height: 22
  },
  assignedBadgeOutbound: {
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    fontWeight: 700,
    fontSize: 11,
    height: 22
  },
  assignedBadgeReturn: {
    backgroundColor: "#ccfbf1",
    color: "#0f766e",
    fontWeight: 700,
    fontSize: 11,
    height: 22
  },
  assignedBadgeOneWay: {
    backgroundColor: "#fef3c7",
    color: "#b45309",
    fontWeight: 700,
    fontSize: 11,
    height: 22
  },
  vehicleInfoRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 600,
    fontSize: 13,
    color: "#1e293b"
  },
  driverInfoRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#334155",
    marginTop: 2
  },
  timeInfoRow: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 3
  },
  lockNote: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#d97706",
    fontSize: 12,
    fontWeight: 500,
    marginTop: 4
  },
  routeBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 600,
    color: "#0f172a"
  },
  emptyState: {
    textAlign: "center",
    padding: "48px 16px",
    color: "#64748b"
  },
  calendarContainer: {
    backgroundColor: "#ffffff",
    padding: 16,
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    "& .fc": {
      fontFamily: '"Sarabun", sans-serif',
      fontSize: 14
    },
    "& .fc-toolbar-title": {
      fontSize: "1.25rem",
      fontWeight: 700,
      color: "#1e293b"
    },
    "& .fc-button": {
      backgroundColor: "#4338ca",
      borderColor: "#4338ca",
      color: "#ffffff",
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      borderRadius: 6,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#3730a3",
        borderColor: "#3730a3"
      },
      "&:focus": {
        boxShadow: "none"
      }
    },
    "& .fc-button-primary:not(:disabled).fc-button-active, & .fc-button-primary:not(:disabled):active": {
      backgroundColor: "#312e81",
      borderColor: "#312e81"
    },
    "& .fc-event": {
      borderRadius: 6,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: 0,
      cursor: "pointer",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      overflow: "hidden"
    },
    "& .fc-event-main": {
      color: "inherit",
      padding: 0
    },
    "& .fc-daygrid-day-number": {
      color: "#334155",
      fontWeight: 600,
      fontSize: 13,
      padding: "4px 8px"
    },
    "& .fc-col-header-cell": {
      backgroundColor: "#f8fafc",
      padding: "8px 0",
      fontWeight: 700,
      color: "#475569"
    }
  },
  calendarLegend: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 16,
    padding: "8px 12px",
    backgroundColor: "#f8fafc",
    borderRadius: 6,
    border: "1px solid #e2e8f0",
    fontSize: 13
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#475569",
    fontWeight: 500
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: "50%"
  }
}));

const statusMeta = {
  pending: {
    label: "รอจัดรถ",
    color: "#fef3c7", // warm soft yellow background
    border: "#f59e0b", // yellow-amber border
    borderAccent: "#d97706", // dark amber accent
    textColor: "#78350f", // deep amber-brown for clear readability on yellow
    badgeBg: "#fde68a",
    bg: "#fef3c7"
  },
  assigned: {
    label: "จัดรถแล้ว",
    color: "#d1fae5", // soft emerald background
    border: "#10b981", // emerald border
    borderAccent: "#059669", // dark emerald accent
    textColor: "#065f46", // deep dark green
    badgeBg: "#a7f3d0",
    bg: "#d1fae5"
  },
  partial: {
    label: "จัดรถบางส่วน",
    color: "#dbeafe", // soft blue background
    border: "#3b82f6", // blue border
    borderAccent: "#2563eb", // dark blue accent
    textColor: "#1e40af", // deep navy blue
    badgeBg: "#bfdbfe",
    bg: "#dbeafe"
  },
  cancelled: {
    label: "ยกเลิกแล้ว",
    color: "#fee2e2", // soft red background
    border: "#ef4444", // red border
    borderAccent: "#dc2626", // dark red accent
    textColor: "#991b1b", // deep dark red
    badgeBg: "#fecaca",
    bg: "#fee2e2"
  }
};

export default function MyVehicle(props) {
  const classes = useStyles();
  const history = useHistory();

  // Current user detection
  const reduxUser = useSelector(state => state.auth?.user);
  const currentUser = reduxUser || getCurrentUser();
  const currentUserName = (currentUser?.userName || "").trim().toLowerCase();

  // Component state
  const [activeTab, setActiveTab] = useState(0); // 0: คำขอของฉัน, 1: การเดินทางของฉัน
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // View mode: "table" | "calendar" (ทั้งสองแท็บสามารถสลับมุมมองปฏิทิน FullCalendar ได้)
  const [viewMode, setViewMode] = useState("table");
  // Selected booking for calendar event click modal
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load bookings from API
  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get("/api/v1/vehiclebooking/assignments/bookings");
      if (response.data?.result === 200 && Array.isArray(response.data.data)) {
        setBookings(response.data.data);
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError(err.response?.data?.msg || err.message || "ไม่สามารถโหลดข้อมูลรายการจองรถได้");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Navigate to Request view for editing
  const handleEdit = booking => {
    if (!booking.canAssign && booking.assignmentStatus !== "pending") return;
    history.push({
      pathname: "/vehicle/request",
      state: { VBID: booking.vbid }
    });
  };

  // Navigate to Request view to create new booking
  const handleCreateNew = () => {
    history.push("/vehicle/request");
  };

  // Helper date formatter
  const formatDate = (dateStr, format = "DD/MM/YYYY") => {
    if (!dateStr) return "-";
    const m = moment(dateStr);
    return m.isValid() ? m.format(format) : "-";
  };

  const formatTime = (startStr, endStr, oneWay) => {
    const s = startStr && moment(startStr).isValid() ? moment(startStr).format("HH:mm") : "-";
    if (oneWay) return `${s} น. (เที่ยวเดียว)`;
    const e = endStr && moment(endStr).isValid() ? moment(endStr).format("HH:mm") : "-";
    return `${s} - ${e} น.`;
  };

  // Filter 1: My Booking Requests (ผู้ขอคนนั้นเป็นคนสร้าง)
  const myRequests = useMemo(() => {
    return bookings.filter(b => {
      const creator = (b.creatorCode || "").trim().toLowerCase();
      // Match creator or if no user is set in test environment, keep visible
      return currentUserName ? creator === currentUserName : true;
    });
  }, [bookings, currentUserName]);

  // Filter 2: My Passenger Trips (เป็นผู้โดยสารในคำขอ)
  const myPassengerTrips = useMemo(() => {
    return bookings.filter(b => {
      const passengers = (b.passengers || []).map(p => (typeof p === "string" ? p.trim().toLowerCase() : ""));
      return currentUserName ? passengers.includes(currentUserName) : true;
    });
  }, [bookings, currentUserName]);

  // Apply search & status filter
  const currentList = activeTab === 0 ? myRequests : myPassengerTrips;

  const filteredList = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return currentList.filter(item => {
      const matchStatus = statusFilter === "all" || item.assignmentStatus === statusFilter;
      if (!matchStatus) return false;
      if (!kw) return true;

      const objective = (item.vbObjective || "").toLowerCase();
      const startPlace = (item.startPlace || "").toLowerCase();
      const endPlace = (item.endPlace || "").toLowerCase();
      const creator = `${item.creatorName || ""} ${item.creatorCode || ""}`.toLowerCase();
      const passengerNames = (item.passengerNames || item.passengers || []).join(" ").toLowerCase();
      const outboundCar = (item.outboundAssignments || []).map(a => `${a.vehicleName || ""} ${a.licensePlate || ""} ${a.driverName || ""}`).join(" ").toLowerCase();
      const returnCar = (item.returnAssignments || []).map(a => `${a.vehicleName || ""} ${a.licensePlate || ""} ${a.driverName || ""}`).join(" ").toLowerCase();

      return (
        objective.includes(kw) ||
        startPlace.includes(kw) ||
        endPlace.includes(kw) ||
        creator.includes(kw) ||
        passengerNames.includes(kw) ||
        outboundCar.includes(kw) ||
        returnCar.includes(kw)
      );
    });
  }, [currentList, search, statusFilter]);

  // Reset page when tab or search changes
  useEffect(() => {
    setPage(0);
  }, [activeTab, search, statusFilter]);

  const displayedRows = filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Prepare events for FullCalendar (สำหรับทั้ง Tab 1: รายการขอใช้รถของฉัน และ Tab 2: การเดินทางของฉัน)
  const calendarEvents = useMemo(() => {
    return filteredList.map(item => {
      const oneWay = isOneWayBooking(item);
      const meta = statusMeta[item.assignmentStatus] || statusMeta.pending;
      const title = item.vbObjective || `${item.startPlace || "-"} ➔ ${item.endPlace || "-"}`;

      return {
        id: item.vbid,
        title: title,
        start: item.startDatetime,
        end: oneWay ? null : (item.endDatetime || item.startDatetime),
        allDay: false,
        backgroundColor: meta.color,
        borderColor: meta.border,
        textColor: meta.textColor,
        extendedProps: {
          booking: item,
          isOneWay: oneWay,
          status: item.assignmentStatus,
          startPlace: item.startPlace,
          endPlace: item.endPlace,
          objective: item.vbObjective
        }
      };
    });
  }, [filteredList]);

  // FullCalendar custom event content (ศึกษาจาก Home.js)
  const renderCalendarEventContent = eventInfo => {
    const { extendedProps } = eventInfo.event;
    const isOneWay = extendedProps.isOneWay;
    const timeDisplay = formatVehicleEventTime(eventInfo);
    const meta = statusMeta[extendedProps.status] || statusMeta.pending;
    const textColor = meta.textColor || "#1e293b";
    const borderAccent = meta.borderAccent || meta.border || "#d97706";

    return (
      <div
        style={{
          padding: "3px 6px",
          fontSize: 12,
          lineHeight: 1.35,
          overflow: "hidden",
          cursor: "pointer",
          color: textColor,
          backgroundColor: meta.color,
          borderLeft: `4px solid ${borderAccent}`,
          borderRadius: 4,
          boxSizing: "border-box",
          width: "100%"
        }}
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
            marginBottom: 2
          }}
        >
          <span style={{ color: textColor }}>{timeDisplay}</span>
          {isOneWay && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                backgroundColor: meta.badgeBg || "rgba(0,0,0,0.08)",
                color: textColor,
                borderRadius: 3,
                padding: "1px 5px",
                border: `1px solid ${borderAccent}`
              }}
            >
              เที่ยวเดียว
            </span>
          )}
        </div>
        <div
          style={{
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: textColor
          }}
        >
          {eventInfo.event.title}
        </div>
        {(extendedProps.startPlace || extendedProps.endPlace) &&
          eventInfo.event.title !== `${extendedProps.startPlace || "-"} ➔ ${extendedProps.endPlace || "-"}` && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: textColor,
                opacity: 0.92
              }}
              title={`เส้นทาง: ${extendedProps.startPlace || "-"} ➔ ${extendedProps.endPlace || "-"}`}
            >
              {extendedProps.startPlace || "-"} ➔ {extendedProps.endPlace || "-"}
            </div>
          )}
      </div>
    );
  };

  // FullCalendar event click handler: เปิด Modal แสดงรายละเอียดคำขอ/การเดินทาง
  const handleEventClick = clickInfo => {
    if (clickInfo?.event?.extendedProps?.booking) {
      setSelectedBooking(clickInfo.event.extendedProps.booking);
    }
  };

  // Render assigned vehicles and drivers cards:
  // - TurnKey = 1: Render 1 Card (round trip - ไป-กลับ)
  // - TurnKey = 2, 3: Render 2 Cards (Card ขาไป & Card ขากลับ)
  // - VehicleBooking.IsOneWay: Display in card that it is "เที่ยวเดียว"
  const renderAssignedVehiclesCards = row => {
    const outbound = row.outboundAssignments || [];
    const inbound = row.returnAssignments || [];
    const isOneWay = isOneWayBooking(row);

    if (outbound.length === 0 && inbound.length === 0) {
      return <span style={{ color: "#94a3b8", fontSize: 13 }}>ยังไม่ได้จัดรถ</span>;
    }

    const allAssignments = [...outbound, ...inbound];
    const hasTurnKey1 = allAssignments.some(a => Number(a.turnKey ?? a.TurnKey) === 1);
    const sharesTurnID =
      outbound.length > 0 &&
      inbound.length > 0 &&
      outbound.some(o => inbound.some(i => i.assignmentTurnID === o.assignmentTurnID));

    // Case 1: VehicleBooking.IsOneWay -> 1 Card with "เที่ยวเดียว"
    if (isOneWay) {
      const leg = outbound[0] || inbound[0] || {};
      const vName = leg.vehicleName || (leg.vehicleKey ? `รถ ${leg.vehicleKey}` : "-");
      const vPlate = leg.licensePlate ? ` (${leg.licensePlate})` : "";
      const dName = leg.driverName || leg.driverUserName || "ยังไม่ระบุ";
      const startTime = leg.startDatetime && moment(leg.startDatetime).isValid()
        ? moment(leg.startDatetime).format("HH:mm")
        : (row.startDatetime && moment(row.startDatetime).isValid() ? moment(row.startDatetime).format("HH:mm") : null);

      return (
        <div className={classes.assignedCard} data-testid="assigned-card-oneway">
          <div className={classes.assignedCardHeader}>
            <Chip
              size="small"
              label="เที่ยวเดียว"
              className={classes.assignedBadgeOneWay}
            />
          </div>
          <div className={classes.vehicleInfoRow}>
            <DirectionsCarIcon fontSize="small" style={{ color: "#4338ca" }} />
            <span>{vName}{vPlate}</span>
          </div>
          <div className={classes.driverInfoRow}>
            <PersonIcon fontSize="small" style={{ color: "#059669" }} />
            <span>ผู้ขับ: {dName}</span>
          </div>
          {startTime && (
            <div className={classes.timeInfoRow}>
              เวลาเดินทาง: {startTime} น. (เที่ยวเดียว)
            </div>
          )}
        </div>
      );
    }

    // Case 2: TurnKey = 1 (ไป-กลับ ใน Turn เดียวกัน -> แสดง Card เดียว ข้อมูลไปกลับ)
    if (hasTurnKey1 || sharesTurnID) {
      const primaryLeg = outbound[0] || inbound[0] || {};
      const outLeg = outbound[0] || primaryLeg;
      const inLeg = inbound[0] || primaryLeg;

      const vName = primaryLeg.vehicleName || (primaryLeg.vehicleKey ? `รถ ${primaryLeg.vehicleKey}` : "-");
      const vPlate = primaryLeg.licensePlate ? ` (${primaryLeg.licensePlate})` : "";
      const dName = primaryLeg.driverName || primaryLeg.driverUserName || "ยังไม่ระบุ";

      const outTime = outLeg.startDatetime && moment(outLeg.startDatetime).isValid()
        ? moment(outLeg.startDatetime).format("HH:mm")
        : (row.startDatetime && moment(row.startDatetime).isValid() ? moment(row.startDatetime).format("HH:mm") : null);
      const inTime = inLeg.endDatetime && moment(inLeg.endDatetime).isValid()
        ? moment(inLeg.endDatetime).format("HH:mm")
        : (inLeg.startDatetime && moment(inLeg.startDatetime).isValid()
          ? moment(inLeg.startDatetime).format("HH:mm")
          : (row.endDatetime && moment(row.endDatetime).isValid() ? moment(row.endDatetime).format("HH:mm") : null));

      return (
        <div className={classes.assignedCard} data-testid="assigned-card-roundtrip">
          <div className={classes.assignedCardHeader}>
            <Chip
              size="small"
              label="ไป-กลับ"
              className={classes.assignedBadgeRoundTrip}
            />
          </div>
          <div className={classes.vehicleInfoRow}>
            <DirectionsCarIcon fontSize="small" style={{ color: "#4338ca" }} />
            <span>{vName}{vPlate}</span>
          </div>
          <div className={classes.driverInfoRow}>
            <PersonIcon fontSize="small" style={{ color: "#059669" }} />
            <span>ผู้ขับ: {dName}</span>
          </div>
          {(outTime || inTime) && (
            <div className={classes.timeInfoRow}>
              เวลาไป-กลับ: {outTime ? `ขาไป ${outTime} น.` : ""}{outTime && inTime ? " • " : ""}{inTime ? `ขากลับ ${inTime} น.` : ""}
            </div>
          )}
        </div>
      );
    }

    // Case 3: TurnKey = 2, 3 (แยกเที่ยวขาไป และ ขากลับ -> แสดง 2 Card ในบรรทัดเดียวกัน)
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          gap: 8,
          flexWrap: "nowrap"
        }}
        data-testid="assigned-cards-row"
      >
        {/* Card ขาไป (TurnKey = 2) */}
        {outbound.length > 0 ? (
          outbound.map((leg, idx) => (
            <div key={leg.assignmentTurnID || `out-${idx}`} className={classes.assignedCard} data-testid="assigned-card-outbound">
              <div className={classes.assignedCardHeader}>
                <Chip size="small" label="ขาไป" className={classes.assignedBadgeOutbound} />
              </div>
              <div className={classes.vehicleInfoRow}>
                <DirectionsCarIcon fontSize="small" style={{ color: "#2563eb" }} />
                <span>{leg.vehicleName || `รถ ${leg.vehicleKey || ""}`}{leg.licensePlate ? ` (${leg.licensePlate})` : ""}</span>
              </div>
              <div className={classes.driverInfoRow}>
                <PersonIcon fontSize="small" style={{ color: "#059669" }} />
                <span>ผู้ขับ: {leg.driverName || leg.driverUserName || "ยังไม่ระบุ"}</span>
              </div>
              {leg.startDatetime && (
                <div className={classes.timeInfoRow}>
                  เวลาขาไป: {moment(leg.startDatetime).format("HH:mm")}{leg.endDatetime ? ` - ${moment(leg.endDatetime).format("HH:mm")}` : ""} น.
                </div>
              )}
            </div>
          ))
        ) : (
          <div className={classes.assignedCardPending} data-testid="assigned-card-outbound-pending">
            <div className={classes.assignedCardHeader}>
              <Chip size="small" label="ขาไป" className={classes.assignedBadgeOutbound} />
            </div>
            <div style={{ color: "#94a3b8", fontSize: 12 }}>ยังไม่ได้จัดรถขาไป</div>
          </div>
        )}

        {/* Card ขากลับ (TurnKey = 3) */}
        {inbound.length > 0 ? (
          inbound.map((leg, idx) => (
            <div key={leg.assignmentTurnID || `ret-${idx}`} className={classes.assignedCard} data-testid="assigned-card-return">
              <div className={classes.assignedCardHeader}>
                <Chip size="small" label="ขากลับ" className={classes.assignedBadgeReturn} />
              </div>
              <div className={classes.vehicleInfoRow}>
                <DirectionsCarIcon fontSize="small" style={{ color: "#0d9488" }} />
                <span>{leg.vehicleName || `รถ ${leg.vehicleKey || ""}`}{leg.licensePlate ? ` (${leg.licensePlate})` : ""}</span>
              </div>
              <div className={classes.driverInfoRow}>
                <PersonIcon fontSize="small" style={{ color: "#059669" }} />
                <span>ผู้ขับ: {leg.driverName || leg.driverUserName || "ยังไม่ระบุ"}</span>
              </div>
              {leg.startDatetime && (
                <div className={classes.timeInfoRow}>
                  เวลาขากลับ: {moment(leg.startDatetime).format("HH:mm")}{leg.endDatetime ? ` - ${moment(leg.endDatetime).format("HH:mm")}` : ""} น.
                </div>
              )}
            </div>
          ))
        ) : (
          <div className={classes.assignedCardPending} data-testid="assigned-card-return-pending">
            <div className={classes.assignedCardHeader}>
              <Chip size="small" label="ขากลับ" className={classes.assignedBadgeReturn} />
            </div>
            <div style={{ color: "#94a3b8", fontSize: 12 }}>ยังไม่ได้จัดรถขากลับ</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Grid>
        <Col xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h4 className={classes.cardTitleWhite}>ระบบข้อมูลการใช้รถของฉัน</h4>
                  <p className={classes.cardCategoryWhite}>
                    ผู้ใช้งาน: <strong>{currentUser?.fullName || currentUser?.FullName || currentUserName || "ผู้ใช้งานระบบ"}</strong>
                  </p>
                </div>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#ffffff", color: "#4338ca", fontWeight: 600 }}
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                >
                  เขียนขอใช้รถใหม่
                </Button>
              </div>
            </CardHeader>

            {/* Tab navigation */}
            <div className={classes.tabsHeader}>
              <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab
                  label={
                    <span className={classes.tabLabel}>
                      รายการขอใช้รถของฉัน ({myRequests.length})
                    </span>
                  }
                  icon={<EventNoteIcon />}
                  aria-label="my-requests-tab"
                />
                <Tab
                  label={
                    <span className={classes.tabLabel}>
                      การเดินทางของฉัน ({myPassengerTrips.length})
                    </span>
                  }
                  icon={<CommuteIcon />}
                  aria-label="my-trips-tab"
                />
              </Tabs>
            </div>

            <CardBody>
              {/* Filter and Search Bar */}
              <div className={classes.filterBar}>
                <div className={classes.filterGroup}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="ค้นหา (สถานที่, วัตถุประสงค์, รถ, คนขับ)..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" style={{ color: "#64748b" }} />
                        </InputAdornment>
                      )
                    }}
                    style={{ minWidth: 280 }}
                  />
                  <Select
                    size="small"
                    variant="outlined"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    style={{ minWidth: 160 }}
                  >
                    <MenuItem value="all">สถานะทั้งหมด</MenuItem>
                    <MenuItem value="pending">รอจัดรถ</MenuItem>
                    <MenuItem value="assigned">จัดรถแล้ว</MenuItem>
                    <MenuItem value="partial">จัดรถบางส่วน</MenuItem>
                    <MenuItem value="cancelled">ยกเลิกแล้ว</MenuItem>
                  </Select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <ButtonGroup size="small" variant="outlined" style={{ backgroundColor: "#ffffff" }}>
                    <Button
                      variant={viewMode === "table" ? "contained" : "outlined"}
                      color={viewMode === "table" ? "primary" : "default"}
                      startIcon={<ViewListIcon fontSize="small" />}
                      onClick={() => setViewMode("table")}
                      style={{ textTransform: "none", fontWeight: 600 }}
                      aria-label="มุมมองตาราง"
                    >
                      ตารางรายการ
                    </Button>
                    <Button
                      variant={viewMode === "calendar" ? "contained" : "outlined"}
                      color={viewMode === "calendar" ? "primary" : "default"}
                      startIcon={<CalendarTodayIcon fontSize="small" />}
                      onClick={() => setViewMode("calendar")}
                      style={{ textTransform: "none", fontWeight: 600 }}
                      aria-label="มุมมองปฏิทิน"
                    >
                      ปฏิทิน
                    </Button>
                  </ButtonGroup>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadBookings}
                    disabled={loading}
                  >
                    รีเฟรชข้อมูล
                  </Button>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <Box p={2} mb={2} bgcolor="#fee2e2" color="#b91c1c" borderRadius={8}>
                  {error}
                </Box>
              )}

              {/* Loading indicator */}
              {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <CircularProgress color="primary" />
                  <Typography variant="body2" style={{ marginTop: 12, color: "#64748b" }}>
                    กำลังโหลดข้อมูลการใช้รถ...
                  </Typography>
                </div>
              ) : displayedRows.length === 0 && viewMode === "table" ? (
                <div className={classes.emptyState}>
                  <CommuteIcon style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 8 }} />
                  <Typography variant="h6" color="textSecondary">
                    {activeTab === 0
                      ? "ไม่พบรายการขอใช้รถของคุณ"
                      : "ไม่พบรายการเดินทางที่คุณเป็นผู้โดยสาร"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {search || statusFilter !== "all"
                      ? "ลองปรับเงื่อนไขการค้นหาหรือตัวกรองสถานะ"
                      : activeTab === 0
                      ? 'คุณสามารถกดปุ่ม "เขียนขอใช้รถใหม่" เพื่อส่งคำขอใช้รถ'
                      : "เมื่อมีคำขอใช้รถที่ระบุชื่อคุณเป็นผู้โดยสาร ข้อมูลจะแสดงที่นี่"}
                  </Typography>
                </div>
              ) : viewMode === "calendar" ? (
                /* CALENDAR VIEW (รองรับทั้ง Tab 1: รายการขอใช้รถของฉัน และ Tab 2: การเดินทางของฉัน) */
                <div className={classes.calendarContainer} data-testid="vehicle-calendar-view">
                  {/* Calendar Legend */}
                  <div className={classes.calendarLegend}>
                    <span style={{ fontWeight: 700, color: "#334155" }}>
                      {activeTab === 0 ? "ปฏิทินรายการขอใช้รถของฉัน" : "ปฏิทินการเดินทางของฉัน"} (
                      {calendarEvents.length} รายการ):
                    </span>
                    <div className={classes.legendItem}>
                      <span className={classes.legendDot} style={{ backgroundColor: statusMeta.pending.borderAccent }} />
                      <span style={{ fontWeight: 600, color: statusMeta.pending.textColor }}>{statusMeta.pending.label}</span>
                    </div>
                    <div className={classes.legendItem}>
                      <span className={classes.legendDot} style={{ backgroundColor: statusMeta.assigned.borderAccent }} />
                      <span style={{ fontWeight: 600, color: statusMeta.assigned.textColor }}>{statusMeta.assigned.label}</span>
                    </div>
                    <div className={classes.legendItem}>
                      <span className={classes.legendDot} style={{ backgroundColor: statusMeta.partial.borderAccent }} />
                      <span style={{ fontWeight: 600, color: statusMeta.partial.textColor }}>{statusMeta.partial.label}</span>
                    </div>
                    <div className={classes.legendItem}>
                      <span className={classes.legendDot} style={{ backgroundColor: statusMeta.cancelled.borderAccent }} />
                      <span style={{ fontWeight: 600, color: statusMeta.cancelled.textColor }}>{statusMeta.cancelled.label}</span>
                    </div>
                  </div>

                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                      start: "prev,next today",
                      center: "title",
                      end: "dayGridMonth,timeGridWeek,timeGridDay,listMonth"
                    }}
                    views={{
                      dayGridMonth: { buttonText: "เดือน" },
                      timeGridWeek: { buttonText: "สัปดาห์" },
                      timeGridDay: { buttonText: "วัน" },
                      listMonth: { buttonText: "รายการ" }
                    }}
                    locale="th"
                    height="75vh"
                    events={calendarEvents}
                    eventContent={renderCalendarEventContent}
                    eventClick={handleEventClick}
                  />
                </div>
              ) : activeTab === 0 ? (
                /* TAB 1: รายการขอใช้รถของฉัน */
                <TableContainer component={Paper} elevation={0} style={{ border: "1px solid #e2e8f0" }}>
                  <Table className={classes.table} size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeadCell} align="center" style={{ width: 50 }}>#</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 140 }}>วันที่เดินทาง</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 320, minWidth: 260 }}>เส้นทาง (ต้นทาง ➔ ปลายทาง)</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 200 }}>วัตถุประสงค์</TableCell>
                        <TableCell className={classes.tableHeadCell} align="center" style={{ width: 90 }}>ผู้โดยสาร</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 120 }}>สถานะการจัดรถ</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 380, minWidth: 320 }}>รถและผู้ขับที่จัดให้</TableCell>
                        <TableCell className={classes.tableHeadCell} align="center" style={{ width: 110 }}>จัดการ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedRows.map((row, index) => {
                        const isAssigned = row.assignmentStatus === "assigned";
                        const canEdit = row.canAssign && row.assignmentStatus === "pending";
                        const meta = statusMeta[row.assignmentStatus] || statusMeta.pending;
                        const oneWay = isOneWayBooking(row);

                        return (
                          <TableRow key={row.vbid || index} hover>
                            <TableCell className={classes.tableCell} align="center">
                              {page * rowsPerPage + index + 1}
                            </TableCell>

                            {/* วันที่และเวลาเดินทาง */}
                            <TableCell className={classes.tableCell}>
                              <div style={{ fontWeight: 600 }}>{formatDate(row.startDatetime)}</div>
                              <div style={{ fontSize: 13, color: "#64748b" }}>
                                {formatTime(row.startDatetime, row.endDatetime, oneWay)}
                              </div>
                            </TableCell>

                            {/* เส้นทาง */}
                            <TableCell className={classes.tableCell} style={{ minWidth: 260 }}>
                              <div className={classes.routeBadge} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                                <span>{row.startPlace || "-"}</span>
                                <ArrowForwardIcon fontSize="inherit" style={{ color: "#64748b" }} />
                                <span>{row.endPlace || "-"}</span>
                              </div>
                            </TableCell>

                            {/* วัตถุประสงค์ */}
                            <TableCell className={classes.tableCell}>
                              <div>{row.vbObjective || "-"}</div>
                              {row.createDatetime && (
                                <Typography variant="caption" style={{ color: "#94a3b8" }}>
                                  ขอเมื่อ: {formatDate(row.createDatetime, "DD/MM/YYYY HH:mm")}
                                </Typography>
                              )}
                            </TableCell>

                            {/* ผู้โดยสาร */}
                            <TableCell className={classes.tableCell} align="center">
                              <Tooltip
                                title={
                                  row.passengerNames && row.passengerNames.length > 0
                                    ? `ผู้โดยสาร: ${row.passengerNames.join(", ")}`
                                    : `ผู้โดยสาร: ${row.totalPassenger ?? 0} คน`
                                }
                              >
                                <span style={{ fontWeight: 600, cursor: "pointer" }}>{row.totalPassenger ?? "-"} คน</span>
                              </Tooltip>
                            </TableCell>

                            {/* สถานะ */}
                            <TableCell className={classes.tableCell}>
                              <Chip
                                size="small"
                                label={meta.label}
                                style={{
                                  backgroundColor: meta.bg,
                                  color: meta.textColor,
                                  fontWeight: 700,
                                  border: `1px solid ${meta.border}`
                                }}
                              />
                            </TableCell>

                            {/* รถและผู้ขับที่จัดให้ */}
                            <TableCell className={classes.tableCell} style={{ minWidth: 320, verticalAlign: "top" }}>
                              {renderAssignedVehiclesCards(row)}
                            </TableCell>

                            {/* ปุ่มแก้ไข / จัดการ */}
                            <TableCell className={classes.tableCell} align="center">
                              {canEdit ? (
                                <Tooltip title="แก้ไขคำขอใช้รถ">
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<EditIcon fontSize="small" />}
                                    onClick={() => handleEdit(row)}
                                    aria-label={`แก้ไขคำขอ ${row.vbObjective || row.vbid}`}
                                  >
                                    แก้ไข
                                  </Button>
                                </Tooltip>
                              ) : (
                                <div>
                                  <Tooltip title="มีการจัดรถแล้ว ไม่สามารถแก้ไขได้ จนกว่าจะมีการยกเลิกการจัดรถ">
                                    <span>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        disabled
                                        startIcon={<LockIcon fontSize="small" />}
                                      >
                                        แก้ไข
                                      </Button>
                                    </span>
                                  </Tooltip>
                                  <div className={classes.lockNote}>
                                    <LockIcon style={{ fontSize: 13 }} />
                                    <span>จัดรถแล้ว</span>
                                  </div>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                /* TAB 2: การเดินทางของฉัน (ผู้โดยสารต้องเดินทาง) */
                <TableContainer component={Paper} elevation={0} style={{ border: "1px solid #e2e8f0" }}>
                  <Table className={classes.table} size="medium">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeadCell} align="center" style={{ width: 50 }}>#</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 140 }}>วันที่เดินทาง</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 140 }}>เวลาเดินทาง</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 320, minWidth: 260 }}>จากไหน ➔ ไปไหน</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ minWidth: 200 }}>ภารกิจ / วัตถุประสงค์</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 220 }}>รถคันไหน</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 180 }}>ใครขับ</TableCell>
                        <TableCell className={classes.tableHeadCell} style={{ width: 140 }}>ผู้ขอใช้รถ</TableCell>
                        <TableCell className={classes.tableHeadCell} align="center" style={{ width: 120 }}>สถานะ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedRows.map((row, index) => {
                        const meta = statusMeta[row.assignmentStatus] || statusMeta.pending;
                        const oneWay = isOneWayBooking(row);

                        // Extract assigned vehicles & drivers from both outbound & return
                        const allLegs = [...(row.outboundAssignments || []), ...(row.returnAssignments || [])];
                        const vehicles = Array.from(
                          new Set(
                            allLegs
                              .map(l => `${l.vehicleName || `รถ ${l.vehicleKey}`}${l.licensePlate ? ` (${l.licensePlate})` : ""}`)
                              .filter(Boolean)
                          )
                        );
                        const drivers = Array.from(
                          new Set(
                            allLegs
                              .map(l => l.driverName || l.driverUserName)
                              .filter(Boolean)
                          )
                        );

                        return (
                          <TableRow key={row.vbid || index} hover>
                            <TableCell className={classes.tableCell} align="center">
                              {page * rowsPerPage + index + 1}
                            </TableCell>

                            {/* วันที่เดินทาง */}
                            <TableCell className={classes.tableCell}>
                              <div style={{ fontWeight: 600 }}>{formatDate(row.startDatetime)}</div>
                              {row.endDatetime && !oneWay && formatDate(row.startDatetime) !== formatDate(row.endDatetime) && (
                                <div style={{ fontSize: 12, color: "#64748b" }}>
                                  ถึง {formatDate(row.endDatetime)}
                                </div>
                              )}
                            </TableCell>

                            {/* เวลาเดินทาง */}
                            <TableCell className={classes.tableCell}>
                              <div style={{ fontWeight: 600, color: "#1e293b" }}>
                                {formatTime(row.startDatetime, row.endDatetime, oneWay)}
                              </div>
                            </TableCell>

                            {/* จากไหน ไปไหน */}
                            <TableCell className={classes.tableCell} style={{ minWidth: 260 }}>
                              <div className={classes.routeBadge} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                                <span>{row.startPlace || "-"}</span>
                                <ArrowForwardIcon fontSize="inherit" style={{ color: "#64748b" }} />
                                <span>{row.endPlace || "-"}</span>
                              </div>
                            </TableCell>

                            {/* วัตถุประสงค์ */}
                            <TableCell className={classes.tableCell}>
                              <div>{row.vbObjective || "-"}</div>
                            </TableCell>

                            {/* รถคันไหน */}
                            <TableCell className={classes.tableCell}>
                              {vehicles.length > 0 ? (
                                vehicles.map((v, i) => (
                                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                    <DirectionsCarIcon fontSize="small" style={{ color: "#4338ca" }} />
                                    <span style={{ fontWeight: 600, color: "#1e293b" }}>{v}</span>
                                  </div>
                                ))
                              ) : (
                                <span style={{ color: "#94a3b8" }}>รอจัดรถ</span>
                              )}
                            </TableCell>

                            {/* ใครขับ */}
                            <TableCell className={classes.tableCell}>
                              {drivers.length > 0 ? (
                                drivers.map((d, i) => (
                                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                    <PersonIcon fontSize="small" style={{ color: "#059669" }} />
                                    <span style={{ fontWeight: 500, color: "#334155" }}>{d}</span>
                                  </div>
                                ))
                              ) : (
                                <span style={{ color: "#94a3b8" }}>รอจัดคนขับ</span>
                              )}
                            </TableCell>

                            {/* ผู้ขอใช้รถ */}
                            <TableCell className={classes.tableCell}>
                              <div style={{ color: "#1e293b", fontWeight: 500 }}>
                                {row.creatorName || row.creatorCode || "-"}
                              </div>
                            </TableCell>

                            {/* สถานะ */}
                            <TableCell className={classes.tableCell} align="center">
                              <Chip
                                size="small"
                                label={meta.label}
                                style={{
                                  backgroundColor: meta.bg,
                                  color: meta.textColor,
                                  fontWeight: 700,
                                  border: `1px solid ${meta.border}`
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Table Pagination */}
              {filteredList.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={e => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage="จำนวนแถวต่อหน้า:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Grid>

      {/* Booking Details Dialog for Calendar Event Click */}
      <Dialog
        open={Boolean(selectedBooking)}
        onClose={() => setSelectedBooking(null)}
        maxWidth="sm"
        fullWidth
        data-testid="calendar-event-dialog"
      >
        <DialogTitle style={{ borderBottom: "1px solid #e2e8f0", padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 700, fontSize: 18, color: "#1e293b" }}>
              {activeTab === 0 ? "รายละเอียดคำขอใช้รถ" : "รายละเอียดการเดินทาง"}
            </span>
            <IconButton size="small" onClick={() => setSelectedBooking(null)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: "20px" }}>
          {selectedBooking && (
            <Box display="flex" flexDirection="column" style={{ gap: 14 }}>
              {/* สถานะ และ ประเภทเที่ยว */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <Chip
                  size="small"
                  label={(statusMeta[selectedBooking.assignmentStatus] || statusMeta.pending).label}
                  style={{
                    backgroundColor: (statusMeta[selectedBooking.assignmentStatus] || statusMeta.pending).bg,
                    color: (statusMeta[selectedBooking.assignmentStatus] || statusMeta.pending).color,
                    fontWeight: 700
                  }}
                />
                {isOneWayBooking(selectedBooking) && (
                  <Chip size="small" label="เที่ยวเดียว" className={classes.assignedBadgeOneWay} />
                )}
              </div>

              {/* วัตถุประสงค์ */}
              <div>
                <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>วัตถุประสงค์</Typography>
                <Typography variant="body1" style={{ fontWeight: 600, color: "#0f172a" }}>
                  {selectedBooking.vbObjective || "-"}
                </Typography>
              </div>

              {/* วันที่และเวลาเดินทาง */}
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>วันที่เดินทาง</Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#1e293b" }}>
                    {formatDate(selectedBooking.startDatetime)}
                    {selectedBooking.endDatetime && !isOneWayBooking(selectedBooking) && formatDate(selectedBooking.startDatetime) !== formatDate(selectedBooking.endDatetime) && (
                      ` ถึง ${formatDate(selectedBooking.endDatetime)}`
                    )}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>เวลาเดินทาง</Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, color: "#1e293b" }}>
                    {formatTime(selectedBooking.startDatetime, selectedBooking.endDatetime, isOneWayBooking(selectedBooking))}
                  </Typography>
                </div>
              </div>

              {/* เส้นทาง */}
              <div>
                <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>เส้นทาง</Typography>
                <div className={classes.routeBadge} style={{ marginTop: 2 }}>
                  <span>{selectedBooking.startPlace || "-"}</span>
                  <ArrowForwardIcon fontSize="inherit" style={{ color: "#64748b" }} />
                  <span>{selectedBooking.endPlace || "-"}</span>
                </div>
              </div>

              {/* ผู้ขอใช้รถ และ จำนวนผู้โดยสาร */}
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>ผู้ขอใช้รถ</Typography>
                  <Typography variant="body2" style={{ color: "#1e293b", fontWeight: 600 }}>
                    {selectedBooking.creatorName || selectedBooking.creatorCode || "-"}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>จำนวนผู้โดยสาร</Typography>
                  <Typography variant="body2" style={{ color: "#334155" }}>
                    {selectedBooking.totalPassenger ?? (selectedBooking.passengers?.length || "-")} คน
                  </Typography>
                </div>
              </div>

              {((selectedBooking.passengerDetails && selectedBooking.passengerDetails.length > 0) ||
                (selectedBooking.passengerNames && selectedBooking.passengerNames.length > 0) ||
                (selectedBooking.passengers && selectedBooking.passengers.length > 0)) && (
                <div>
                  <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>รายชื่อผู้โดยสาร</Typography>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {(selectedBooking.passengerDetails || selectedBooking.passengers || []).map((p, idx) => {
                      const displayName =
                        typeof p === "object"
                          ? (p.fullName || p.userName)
                          : (selectedBooking.passengerNames?.[idx] || p);
                      return (
                        <Chip
                          key={idx}
                          size="small"
                          label={displayName}
                          icon={<PersonIcon style={{ fontSize: 16, color: "#475569" }} />}
                          style={{ backgroundColor: "#f1f5f9", color: "#1e293b", fontWeight: 500 }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <Divider style={{ margin: "4px 0" }} />

              {/* รถและผู้ขับที่จัดให้ */}
              <div>
                <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
                  รถและผู้ขับที่จัดให้
                </Typography>
                {renderAssignedVehiclesCards(selectedBooking)}
              </div>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px", borderTop: "1px solid #e2e8f0" }}>
          {activeTab === 0 && selectedBooking && selectedBooking.canAssign && selectedBooking.assignmentStatus === "pending" && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => {
                const b = selectedBooking;
                setSelectedBooking(null);
                handleEdit(b);
              }}
            >
              แก้ไขคำขอ
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSelectedBooking(null)}
          >
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

MyVehicle.propTypes = {
  history: PropTypes.object
};
